/**
 * Werknemers Service - API calls voor werknemers
 */

import apiFetch from './api';

export enum ContractType {
  VAST = 'Vast',
  TIJDELIJK = 'Tijdelijk',
  OPROEP = 'Oproep',
  ZZP = 'ZZP',
  UITZEND = 'Uitzend',
  STAGE = 'Stage',
}

export interface Role {
  id: number;
  naam: string;
  omschrijving?: string;
}

export interface Werknemer {
  id: number;
  personeelsnummer?: string;
  aanhef?: string;
  voornaam: string;
  tussenvoegsel?: string;
  achternaam: string;
  email?: string;
  telefoon?: string;
  mobiel?: string;
  straat?: string;
  huisnummer?: string;
  postcode?: string;
  plaats?: string;
  land: string;
  geboortedatum?: string;
  bsn?: string;
  contract_type?: ContractType;
  datum_in_dienst?: string;
  datum_uit_dienst?: string;
  contract_einddatum?: string;
  uren_per_week?: number;
  functie?: string;
  afdeling?: string;
  cao?: string;
  bruto_uurloon?: number;
  basisuurloon?: number;
  reiskostenvergoeding?: number;
  expertises?: string;
  certificaten?: string;
  noodcontact_naam?: string;
  noodcontact_telefoon?: string;
  noodcontact_relatie?: string;
  notities?: string;
  actief: boolean;
  // Login gegevens
  username?: string;
  rol_id?: number;
  rol?: Role;
  is_superuser: boolean;
  created_at: string;
  updated_at?: string;
}

export interface WerknemerCreate {
  personeelsnummer?: string;
  aanhef?: string;
  voornaam: string;
  tussenvoegsel?: string;
  achternaam: string;
  email?: string;
  telefoon?: string;
  mobiel?: string;
  straat?: string;
  huisnummer?: string;
  postcode?: string;
  plaats?: string;
  land?: string;
  geboortedatum?: string;
  bsn?: string;
  contract_type?: ContractType;
  datum_in_dienst?: string;
  datum_uit_dienst?: string;
  contract_einddatum?: string;
  uren_per_week?: number;
  functie?: string;
  afdeling?: string;
  cao?: string;
  bruto_uurloon?: number;
  basisuurloon?: number;
  reiskostenvergoeding?: number;
  expertises?: string;
  certificaten?: string;
  noodcontact_naam?: string;
  noodcontact_telefoon?: string;
  noodcontact_relatie?: string;
  notities?: string;
  actief?: boolean;
  // Login gegevens
  username?: string;
  password?: string;
  rol_id?: number;
}

export interface WerknemerListResponse {
  items: Werknemer[];
  total: number;
  page: number;
  page_size: number;
}

export interface WerknemerFilters {
  skip?: number;
  limit?: number;
  actief?: boolean;
  zoek?: string;
  afdeling?: string;
  contract_type?: string;
  sorteer?: string;
  richting?: string;
}

// Helper functie om lege strings en undefined te verwijderen uit data
const cleanWerknemerData = (data: any): any => {
  const cleaned: any = {};
  for (const key in data) {
    // Skip inherited properties
    if (!data.hasOwnProperty(key)) continue;

    const value = data[key];

    // Verwijder undefined waarden
    if (value === undefined) {
      continue;
    }
    // Verwijder lege strings voor datum velden
    if (value === '' && (key.includes('datum') || key === 'geboortedatum')) {
      continue;
    }
    // Verwijder andere lege strings ook
    if (value === '') {
      continue;
    }
    // Stuur het veld mee
    cleaned[key] = value;
  }
  return cleaned;
};

export const werknemersService = {
  async getAll(filters: WerknemerFilters = {}): Promise<WerknemerListResponse> {
    const params = new URLSearchParams();

    if (filters.skip !== undefined) params.append('skip', filters.skip.toString());
    if (filters.limit !== undefined) params.append('limit', filters.limit.toString());
    if (filters.actief !== undefined) params.append('actief', filters.actief.toString());
    if (filters.zoek) params.append('zoek', filters.zoek);
    if (filters.afdeling) params.append('afdeling', filters.afdeling);
    if (filters.contract_type) params.append('contract_type', filters.contract_type);
    if (filters.sorteer) params.append('sorteer', filters.sorteer);
    if (filters.richting) params.append('richting', filters.richting);

    return apiFetch(`/api/v1/werknemers?${params.toString()}`);
  },

  async getById(id: number): Promise<Werknemer> {
    return apiFetch(`/api/v1/werknemers/${id}`);
  },

  async create(data: WerknemerCreate): Promise<Werknemer> {
    const cleanedData = cleanWerknemerData(data);
    return apiFetch('/api/v1/werknemers', {
      method: 'POST',
      body: JSON.stringify(cleanedData),
    });
  },

  async update(id: number, data: Partial<WerknemerCreate>): Promise<Werknemer> {
    const cleanedData = cleanWerknemerData(data);
    return apiFetch(`/api/v1/werknemers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(cleanedData),
    });
  },

  async delete(id: number): Promise<void> {
    return apiFetch(`/api/v1/werknemers/${id}`, {
      method: 'DELETE',
    });
  },

  async getRoles(): Promise<Role[]> {
    return apiFetch('/api/v1/werknemers/roles/list');
  },
};
